import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import Button from "../../../components/Button";
import EntityPicker from "../../../components/EntityPicker";
import FormField from "../../../components/FormField";
import TextInput from "../../../components/TextInput";
import TextArea from "../../../components/TextArea";
import { useLieuxList } from "../../../../hooks/useLieuxList";
import { useCreateLieu } from "../../../../hooks/useCreateLieu";
import { useUpdateParcours } from "../../../../hooks/useUpdateParcours";
import { parcoursResponseToDto } from "../toDto";
import InlineLieuEditor from "./InlineLieuEditor";
import type { LieuMiniResponse, ParcoursResponse } from "../../../../api/parcoursApi";

const EMPTY_NEW = { name: "", description: "", addressName: "", street: "", postcode: "", city: "" };

export default function ParcoursLieuxTab() {
  const { id = "" } = useParams<{ id: string }>();
  const parcours = useOutletContext<ParcoursResponse>();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_NEW);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const list = useLieuxList({ search, page: 0, size: 30, sort: "name,asc" });
  const create = useCreateLieu();
  const update = useUpdateParcours(id);

  const removeLieu = (lieuId: string) => {
    const next = parcoursResponseToDto(parcours);
    next.lieuIds = (next.lieuIds ?? []).filter((lid) => lid !== lieuId);
    if (expandedId === lieuId) setExpandedId(null);
    update.mutate(next);
  };

  const addLieu = (lieuId: string) => {
    const next = parcoursResponseToDto(parcours);
    next.lieuIds = Array.from(new Set([...(next.lieuIds ?? []), lieuId]));
    update.mutate(next);
  };

  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    create.mutate(
      {
        name: draft.name.trim(),
        description: draft.description || undefined,
        addressName: draft.addressName || undefined,
        address: {
          street: draft.street || null,
          postcode: draft.postcode || null,
          city: draft.city || null,
        },
      },
      {
        onSuccess: (created) => {
          addLieu(created.id);
          setDraft(EMPTY_NEW);
          setCreating(false);
        },
      },
    );
  };

  const pickerOptions =
    list.data?.content.map((l) => ({
      id: l.id,
      label: l.name,
      sublabel: l.addressName || [l.address?.street, l.address?.city].filter(Boolean).join(" · "),
    })) ?? [];
  const attached = parcours.lieux ?? [];
  const assignedIds = attached.map((l) => l.id);

  return (
    <div className="bo-stack">
      <div className="bo-card">
        <h3 style={{ margin: 0, marginBottom: "var(--sp-4)", fontFamily: "var(--font-display)", fontSize: 18 }}>
          Lieux du parcours ({attached.length})
        </h3>
        {attached.length === 0 ? (
          <p style={{ color: "var(--ink-mute)", fontSize: 13, margin: 0 }}>
            Aucun lieu pour le moment.
          </p>
        ) : (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--sp-2)",
            }}
          >
            {attached.map((l) => (
              <LieuRow
                key={l.id}
                lieu={l}
                expanded={expandedId === l.id}
                onToggle={() => setExpandedId(expandedId === l.id ? null : l.id)}
                onRemove={() => removeLieu(l.id)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="bo-card">
        <h3 style={{ margin: 0, marginBottom: "var(--sp-3)", fontFamily: "var(--font-display)", fontSize: 18 }}>
          Ajouter un lieu
        </h3>

        <EntityPicker
          placeholder="Rechercher un lieu par nom, ville…"
          options={pickerOptions}
          excludeIds={assignedIds}
          onSearchChange={setSearch}
          onPick={(o) => addLieu(o.id)}
          loading={list.isLoading}
        />

        {!creating && (
          <div style={{ marginTop: "var(--sp-4)" }}>
            <Button type="button" variant="secondary" onClick={() => setCreating(true)}>
              + Créer un nouveau lieu
            </Button>
          </div>
        )}

        {creating && (
          <form
            onSubmit={submitNew}
            style={{ marginTop: "var(--sp-4)", paddingTop: "var(--sp-4)", borderTop: "1px solid var(--line-soft)" }}
            className="bo-stack"
          >
            <FormField label="Nom" required>
              <TextInput value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required autoFocus />
            </FormField>
            <FormField label="Description">
              <TextArea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </FormField>
            <FormField label="Nom de l'adresse" hint="Libellé libre affiché si l'adresse structurée n'est pas remplie.">
              <TextInput value={draft.addressName} onChange={(e) => setDraft({ ...draft, addressName: e.target.value })} />
            </FormField>
            <div className="bo-grid-3">
              <FormField label="Rue">
                <TextInput value={draft.street} onChange={(e) => setDraft({ ...draft, street: e.target.value })} />
              </FormField>
              <FormField label="Code postal">
                <TextInput value={draft.postcode} onChange={(e) => setDraft({ ...draft, postcode: e.target.value })} />
              </FormField>
              <FormField label="Ville">
                <TextInput value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
              </FormField>
            </div>
            <div style={{ display: "flex", gap: "var(--sp-2)", justifyContent: "flex-end" }}>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" loading={create.isPending}>
                Créer et ajouter au parcours
              </Button>
            </div>
          </form>
        )}

        {update.isError && (
          <p className="bo-error-text" style={{ marginTop: "var(--sp-3)" }}>
            Erreur : {(update.error as Error)?.message}
          </p>
        )}
      </div>
    </div>
  );
}

interface LieuRowProps {
  lieu: LieuMiniResponse;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
}

function LieuRow({ lieu, expanded, onToggle, onRemove }: LieuRowProps) {
  const addressLabel =
    lieu.addressName || [lieu.address?.street, lieu.address?.city].filter(Boolean).join(" · ");
  return (
    <li
      style={{
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--radius-s)",
        background: "var(--paper)",
        padding: "var(--sp-3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-3)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: "var(--ink)" }}>{lieu.name}</div>
          {addressLabel && (
            <div
              style={{
                fontSize: 12,
                color: "var(--ink-mute)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {addressLabel}
            </div>
          )}
        </div>
        <div className="bo-row-end">
          <Button variant="ghost" onClick={onToggle}>
            {expanded ? "Replier" : "Éditer"}
          </Button>
          <Button variant="ghost" onClick={onRemove}>
            Retirer
          </Button>
        </div>
      </div>
      {expanded && <InlineLieuEditor lieuId={lieu.id} onClose={onToggle} />}
    </li>
  );
}
